import { createStyles } from 'antd-style';
import React, { useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';

interface RichTextEditorProps {
  /** 当前值（HTML 字符串） */
  value?: string;
  /** 值变化回调 */
  onChange?: (value: string) => void;
  /** 占位符 */
  placeholder?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 编辑器高度 */
  height?: number;
  /** 是否只读 */
  readOnly?: boolean;
}

const useStyles = createStyles(({ token }) => ({
  container: {
    width: '100%',
    '.ql-toolbar': {
      borderTopLeftRadius: token.borderRadius,
      borderTopRightRadius: token.borderRadius,
      borderColor: token.colorBorder,
      backgroundColor: token.colorBgContainer,
    },
    '.ql-container': {
      borderBottomLeftRadius: token.borderRadius,
      borderBottomRightRadius: token.borderRadius,
      borderColor: token.colorBorder,
      backgroundColor: token.colorBgContainer,
      fontSize: token.fontSize,
    },
    '.ql-editor': {
      minHeight: 'var(--editor-min-height, 200px)',
    },
    '.ql-editor.ql-blank::before': {
      color: token.colorTextPlaceholder,
      fontStyle: 'normal',
    },
    // 禁用状态
    '&.disabled': {
      '.ql-toolbar': {
        backgroundColor: token.colorBgContainerDisabled,
        pointerEvents: 'none',
        opacity: 0.6,
      },
      '.ql-container': {
        backgroundColor: token.colorBgContainerDisabled,
      },
      '.ql-editor': {
        cursor: 'not-allowed',
      },
    },
  },
}));

// 工具栏配置
const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['link', 'image'],
  ['blockquote', 'code-block'],
  ['clean'],
];

/**
 * 富文本编辑器组件
 * 基于 react-quill-new，支持 React 19
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = '请输入内容...',
  disabled = false,
  height = 300,
  readOnly = false,
}) => {
  const { styles, cx } = useStyles();

  const handleChange = (content: string) => {
    // Quill 在空白时会返回 '<p><br></p>'，将其转换为空字符串
    const isEmpty = content === '<p><br></p>' || content === '<p></p>' || !content.trim();
    onChange?.(isEmpty ? '' : content);
  };

  const modules = useMemo(
    () => ({
      toolbar: TOOLBAR_OPTIONS,
    }),
    [],
  );

  // 动态样式：设置编辑器最小高度
  const containerStyle = useMemo(
    () =>
      ({
        '--editor-min-height': `${height}px`,
      }) as React.CSSProperties,
    [height],
  );

  return (
    <div
      className={cx(styles.container, disabled && 'disabled')}
      style={containerStyle}
    >
      <ReactQuill
        theme="snow"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        readOnly={disabled || readOnly}
        modules={modules}
      />
    </div>
  );
};

export default RichTextEditor;
